import { LatLng, LatLngBounds } from 'leaflet'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  AttributionControl,
  MapContainer,
  TileLayer,
  useMap,
  useMapEvents
} from 'react-leaflet'

import { CLASS_PREFIX } from 'src/constants/'
import * as MAP from 'src/constants/map'
import { IBoundingBox, IPosition } from 'src/interfaces/location'
import { boundsAround, zoomFloorOf } from 'src/utils/map/view_limits'
import { getToggle } from 'src/utils/toggle'

import { IMapStyleToggle } from 'src/interfaces/'

import FeatureLayer from 'src/components/map/feature_layer/feature_layer'
import 'src/components/map/project_map/project_map.scss'

const cls = CLASS_PREFIX + 'project-map'

const defaultMapStyle: IMapStyleToggle = {
  styleURL: MAP.MAP_DEFAULT_MAPBOX_STYLE
}

const mapStyleToggle = getToggle<IMapStyleToggle>('mapStyle', defaultMapStyle)

const tileLayerUrl =
  `https://{s}.tiles.mapbox.com/styles/v1/${mapStyleToggle.styleURL}` +
  `/tiles/{z}/{x}/{y}?access_token=${MAP.MAP_TOKEN}`

interface IProps {
  geojson: GeoJSON.GeoJsonObject;
  boundingBox?: IBoundingBox;
  center?: IPosition;
  zoom?: number;
  interactive?: boolean;
  className?: string;
}

/**
 * Fits the map to the project's bounding box and keeps the view around it.
 *
 * Done from inside the map rather than through props, because `MapContainer` builds the map once
 * with the options of the first render and reads none of them again — and the zoom the box fits
 * at is not known before the map exists, since it depends on the size of the container.
 *
 * The fit happens once, as it did before, and the limits come out of that same fit, so the two
 * can never describe different boxes. Three things in here carry:
 *
 * 1. **The fitted zoom is asked for, not read off the map afterwards.** `getBoundsZoom` is the
 *    number `fitBounds` itself works from; reading `getZoom()` after the fit would tie the bound
 *    to the fit never being animated.
 * 2. **Nothing happens until both axes of the container have a size.** A map that is mounted
 *    while hidden has none, and a fit without one lands on zoom 0: the scale comes out as zero
 *    in whichever axis is missing, and the zoom that follows from it is clamped up from minus
 *    infinity to the world's own level. The map then shows no tiles at all, because the tile
 *    layer asks for the level below the one it is given. Leaflet's own `resize` brings the map
 *    back here once a size arrives through a window resize, which is how every caller's map
 *    becomes visible today, since all of them hide theirs behind a media query. A map that were
 *    shown some other way, by a tab or a fold, would stay unfitted.
 * 3. **`maxBounds` is rebuilt for every size, the lower bound is not.** Leaflet pins an axis
 *    whose view is wider than `maxBounds` to the middle of it, so bounds that fit one container
 *    hold the map still in a larger one — turning a phone or widening a window is enough. The
 *    bound itself stays: `getBoundsZoom` clamps to the minimum currently set, so a second fit
 *    would have to widen it first, and a raised minimum pulls a visitor who is below it through
 *    an animated clamp.
 *
 * A project without a bounding box gets neither, on purpose: its features lie where the project
 * lies, while the map starts over the middle of the country, and a bound there would only make
 * them harder to reach.
 */
const FitAndLimit: React.FC<{ boundingBox: IBoundingBox }> = ({ boundingBox }) => {
  const map = useMap()
  // What the first fit with a usable container produced: the centre the box ended up on and the
  // bound derived from it. Null for as long as no such fit has happened.
  const fit = useRef<{ center: LatLng; floor: number } | null>(null)

  const bounds = useMemo(
    () => (boundingBox.northEast && boundingBox.southWest
      ? new LatLngBounds(
        [boundingBox.southWest.latitude, boundingBox.southWest.longitude],
        [boundingBox.northEast.latitude, boundingBox.northEast.longitude]
      )
      : undefined),
    [boundingBox]
  )

  const apply = useCallback(() => {
    const size = map.getSize()
    if (!bounds || size.x <= 0 || size.y <= 0) {
      return
    }

    if (fit.current) {
      map.setMaxBounds(boundsAround(
        fit.current.center,
        size,
        fit.current.floor,
        MAP.MAP_PROJECT_MAX_BOUNDS_PADDING,
        map.options.crs
      ))

      return
    }

    const floor = zoomFloorOf(map.getBoundsZoom(bounds))
    map.fitBounds(bounds, { animate: false })
    const center = map.getCenter()
    map.setMinZoom(floor)
    map.setMaxBounds(boundsAround(
      center,
      size,
      floor,
      MAP.MAP_PROJECT_MAX_BOUNDS_PADDING,
      map.options.crs
    ))
    fit.current = { center, floor }
  }, [map, bounds])

  useEffect(() => {
    apply()
  }, [apply])

  // Kept stable: react-leaflet re-subscribes whenever this object changes identity.
  const mapHandlers = useMemo(() => ({ resize: apply }), [apply])
  useMapEvents(mapHandlers)

  return null
}

const ProjectMap: React.FC<IProps> = ({
  geojson,
  boundingBox,
  center,
  zoom = MAP.MAP_DEFAULT_ZOOM,
  interactive = true,
  className
}) => {
  const initialCenter = center
    ? [center.latitude, center.longitude] as [number, number]
    : boundingBox
      ? [
        (boundingBox.northEast.latitude + boundingBox.southWest.latitude) / 2,
        (boundingBox.northEast.longitude + boundingBox.southWest.longitude) / 2
      ] as [number, number]
      : [MAP.CENTER_OF_GERMANY_POSITION.latitude, MAP.CENTER_OF_GERMANY_POSITION.longitude] as [number, number]

  return (
    <div className={`${cls}${className ? ' ' + className : ''}`}>
      <MapContainer
        attributionControl={MAP.MAP_OPTION_ATTRIBUTION_CONTROL}
        boxZoom={interactive}
        className={cls + '__container'}
        center={initialCenter}
        doubleClickZoom={interactive}
        dragging={interactive}
        keyboard={interactive}
        scrollWheelZoom={interactive}
        touchZoom={interactive}
        zoomControl={false}
        zoom={zoom}
      >
        <AttributionControl position={'topright'} prefix="" />
        <TileLayer
          url={tileLayerUrl}
          attribution={MAP.MAP_ATTRIBUTION}
          tileSize={MAP.MAP_OPTION_TILE_SIZE}
          zoomOffset={MAP.MAP_OPTION_ZOOM_OFFSET}
        />
        {/* The zoom range applies here as it does on the overview: a detail shape waits for its
            `minzoom`, a coarse one gives way at its `maxzoom`, and a picture answers to the same
            range. This one component serves all three places a project map appears, the preview on
            the project page, the page itself and the full map page. The stand-in point catches
            what is left when nothing can be drawn at all: a document whose shapes sit outside the
            current zoom, or one whose carriers name no picture a browser can load. */}
        <FeatureLayer geojson={geojson} standInWhenEmpty />
        {boundingBox && <FitAndLimit boundingBox={boundingBox} />}
      </MapContainer>
    </div>
  )
}

export default ProjectMap
